import { useEffect, useState } from 'react';
import { Users, ShieldCheck, UserCheck, MailWarning, Rss, Radio, FileText, type LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminApi } from '@/features/admin/services/adminApi';
import { getErrorMessage } from '@/shared/services/api';
import { AdminStats } from '@/shared/types/api';
import { Spinner } from '@/shared/components/Spinner';

interface StatCard {
  label: string;
  value: number;
  icon: LucideIcon;
}

export function AdminOverview() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await adminApi.getStats();
        if (active) setStats(data);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load statistics'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (!stats) return null;

  const cards: StatCard[] = [
    { label: 'Total users', value: stats.totalUsers, icon: Users },
    { label: 'Admins', value: stats.admins, icon: ShieldCheck },
    { label: 'Active users', value: stats.activeUsers, icon: UserCheck },
    { label: 'Pending confirmation', value: stats.pendingConfirmation, icon: MailWarning },
    { label: 'Total feeds', value: stats.totalFeeds, icon: Rss },
    { label: 'Active feeds', value: stats.activeFeeds, icon: Radio },
    { label: 'Feed items', value: stats.totalFeedItems, icon: FileText },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="card flex items-center gap-4 p-5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight text-foreground">{card.value}</p>
              <p className="text-sm text-muted-foreground">{card.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
